import { isValidElement, ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { IconBaseProps } from 'react-icons';
import { TbColumns } from 'react-icons/tb';

import { useListStore } from '@/stores';
import { ListViewColumn } from '@/types';

import { createIntegrationMaps } from './maps/integrationMaps';
import { useAntDesignTheme } from './useAntDesignTheme';

export interface ListColumnIconInput extends Pick<ListViewColumn, 'key' | 'dataType'> {
    icon?: ReactNode;
    src?: string;
}

export interface ListColumnIcon {
    icon: ReactElement<IconBaseProps>;
    src?: string;
}

/** Resolves persisted and newly added columns from the same field, provider, and data type maps. */
export const useListColumnIcons = () => {
    const { token } = useAntDesignTheme();
    const { selectedList } = useListStore();
    const maps = useMemo(() => createIntegrationMaps(token), [token]);
    const sourceIcons = useMemo(
        () =>
            new Map(
                (selectedList?.dataSources ?? []).flatMap(({ connectedAccount }) =>
                    connectedAccount?.id && connectedAccount.type
                        ? [[connectedAccount.id, maps.integrationLabelToIconMap[connectedAccount.type]] as const]
                        : [],
                ),
            ),
        [maps, selectedList?.dataSources],
    );

    const getColumnIcon = useCallback(
        (column: ListColumnIconInput): ListColumnIcon => {
            const [namespace, accountId] = column.key.split(':');
            const src = column.src ?? (namespace === 'dataSource' ? sourceIcons.get(accountId) : undefined);
            const icon =
                maps.listColumnKeyToIconMap[column.key] ??
                maps.listColumnDataTypeToIconMap[column.dataType] ??
                column.icon;

            return { src, icon: isValidElement<IconBaseProps>(icon) ? icon : <TbColumns /> };
        },
        [maps, sourceIcons],
    );

    const getColumnAvatar = useCallback(
        (column: ListColumnIconInput) => {
            const { icon, src } = getColumnIcon(column);

            return {
                icon,
                src: src ? (
                    <img alt='' src={src} style={{ width: '75%', height: '75%', objectFit: 'contain' }} />
                ) : undefined,
            };
        },
        [getColumnIcon],
    );

    return { getColumnIcon, getColumnAvatar };
};
